import React, { useEffect, useRef, useState } from 'react';
import { fetchTeachers, createTeacher, deleteTeacher } from '../../utils/accountsApi';
import './Classes.css';
import './Teachers.css';
import Footer from '../Footer';

const MAX_TEACHER_ACCOUNTS = 25;

function CreateTeacherRow({ onCancel, onCreated }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    if (!trimmedFirstName && !trimmedLastName && !trimmedEmail) { onCancel(); return; }
    if (!trimmedFirstName) { setError('First name is required.'); return; }
    if (!trimmedLastName) { setError('Last name is required.'); return; }
    if (!trimmedEmail) { setError('Email is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await createTeacher(trimmedFirstName, trimmedLastName, trimmedEmail);
      onCreated(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Could not create the teacher account. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="class-card class-card-creating">
      <div className="class-card-main">
        <div className="teacher-create-fields">
          <input
            ref={inputRef}
            className="review-name-input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            disabled={saving}
            required
          />
          <input
            className="review-name-input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            disabled={saving}
            required
          />
          <input
            type="email"
            className="review-name-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            disabled={saving}
            required
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') onCancel(); }}
          />
        </div>
        {error && <span className="review-field-error">{error}</span>}
      </div>
      <div className="class-card-actions">
        <button className="title-button" onClick={commit} disabled={saving}>
          {saving ? 'Creating...' : 'Create'}
        </button>
        <button className="upload-cancel-btn" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </div>
  );
}

function TeacherRow({ teacher, onDeleted }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteTeacher(teacher.id);
      onDeleted(teacher.id);
    } catch {
      alert('Could not delete this teacher account. Please try again.');
      setDeleting(false);
    }
  };

  return (
    <div className="class-card">
      <div className="class-card-main">
        <div className="class-name-row">
          <span className="class-name">{teacher.name}</span>
        </div>
        <span className="class-meta-item">{teacher.email}</span>
      </div>
      <div className="class-card-actions">
        {confirmDelete ? (
          <div className="delete-confirm">
            <span className="delete-confirm-text">Delete this account?</span>
            <button className="delete-confirm-yes" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button className="delete-confirm-no" onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</button>
          </div>
        ) : (
          <button className="class-remove-btn" onClick={() => setConfirmDelete(true)} title="Delete account">×</button>
        )}
      </div>
    </div>
  );
}

function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchTeachers()
      .then((res) => { if (Array.isArray(res.data)) setTeachers(res.data); })
      .catch(() => setTeachers([]))
      .finally(() => setLoading(false));
  }, []);

  const handleCreated = (newTeacher) => {
    setTeachers((prev) => [...prev, newTeacher]);
    setCreating(false);
  };

  const handleDeleted = (id) => {
    setTeachers((prev) => prev.filter((t) => t.id !== id));
  };

  const atCapacity = teachers.length >= MAX_TEACHER_ACCOUNTS;

  return (
    <div className="root-container">
      <div className="title-container">
        <div className="title-left">
          <div className="title-name-container">
            <header className="name-header">Teachers</header>
          </div>
        </div>
        <div className="reload-button-container" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!loading && (
            <span className="account-count-indicator">
              {teachers.length}/{MAX_TEACHER_ACCOUNTS} accounts
            </span>
          )}
          <button
            className="reload-button"
            onClick={() => setCreating(true)}
            disabled={creating || atCapacity}
            title={atCapacity ? `Maximum of ${MAX_TEACHER_ACCOUNTS} teacher accounts reached` : undefined}
          >
            + Add Teacher
          </button>
        </div>
      </div>

      <div className="component-container">
        <div className="classes-content">
          {!loading && atCapacity && (
            <p className="chat-card-hint">
              Maximum of {MAX_TEACHER_ACCOUNTS} teacher accounts reached — delete one to add another.
            </p>
          )}
          {loading ? (
            <div className="cases-loading">Loading teachers...</div>
          ) : (
            <div className="classes-list">
              {creating && !atCapacity && (
                <CreateTeacherRow onCancel={() => setCreating(false)} onCreated={handleCreated} />
              )}
              {teachers.length === 0 && !creating && (
                <p className="chat-card-hint">No teacher accounts yet — click "+ Add Teacher" to create one.</p>
              )}
              {teachers.map((teacher) => (
                <TeacherRow
                  key={teacher.id}
                  teacher={teacher}
                  onDeleted={handleDeleted}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Teachers;
